import { FC, useState } from 'react';

import { RootState } from 'store/rootReducer';

import { useAppSelector } from 'hooks/defaultStoreHooks';

import { Colors } from 'components/map/Styles';
import { hourglassCircleSvg, penCircleSvg } from 'components/map/icons';
import { TextButton } from 'components/sharedElements/sharedElements';

import { BebyggelseobjektUtkast } from 'models';

import { RegistreringsstatustypEnum } from 'enums';

import classNames from 'classnames';
import { formatDateWithTime } from 'utilities/dateUtilities';
import { notReservedOrReservedByMe } from 'utilities/notReservedOrReservedByMe';

export interface UtkastStatusSectionProps {
  utkast: BebyggelseobjektUtkast;
}

const UtkastStatusSection: FC<UtkastStatusSectionProps> = ({ utkast }) => {
  const user = useAppSelector((state: RootState) => state.user);

  const [expanded, setExpanded] = useState<boolean>(false);

  const resolveUtkastIcon = (status: RegistreringsstatustypEnum): string => {
    return status === RegistreringsstatustypEnum.REDIGERBAR
      ? `data:image/svg+xml,${encodeURIComponent(penCircleSvg(Colors.GREEN))}`
      : `data:image/svg+xml,${encodeURIComponent(hourglassCircleSvg(Colors.GREEN))}`;
  };

  return (
    <div className="tw-p-4 md:tw-p-6">
      {utkast.registrering.registreringsstatustyp_id === RegistreringsstatustypEnum.REDIGERBAR && (
        <div className="tw-border-2 tw-border-raa-green tw-p-3">
          <div className="tw-flex">
            <img
              src={resolveUtkastIcon(utkast.registrering.registreringsstatustyp_id)}
              alt="Ikon för ej publicerat utkast"
              className="tw-mr-2 tw-h-6"
            />
            <span className="tw-grow tw-text-raa-green">
              Utkast: {utkast.registrering.anvandarnamn}{' '}
              {utkast.registrering.senast_andrad && utkast.registrering.senast_andrad.toString().split('T')[0]}{' '}
              {utkast.aktivitetref?.aktivitetsnamn && <>({utkast.aktivitetref?.aktivitetsnamn})</>}
            </span>
            {!notReservedOrReservedByMe(user, utkast.registrering) && <TextButton icon="raa-icon-lock-closed" />}
          </div>
          {utkast.anteckning && (
            <div className="tw-pt-2">
              <TextButton
                icon={expanded ? 'raa-icon-arrow-up' : 'raa-icon-arrow-down'}
                aria-expanded={expanded}
                onClick={() => setExpanded(!expanded)}
              >
                Visa anteckning
              </TextButton>
              <p aria-hidden={!expanded} className={classNames('tw-pt-2', { 'tw-hidden': !expanded })}>
                {utkast.anteckning}
              </p>
            </div>
          )}
        </div>
      )}
      {utkast.registrering.registreringsstatustyp_id === RegistreringsstatustypEnum.INSKICKAD && (
        <div className="tw-border-2 tw-border-dashed tw-border-raa-green tw-p-3">
          <div className="tw-flex">
            <img
              src={`data:image/svg+xml,${encodeURIComponent(hourglassCircleSvg(Colors.GREEN))}`}
              alt="Ikon för ej publicerat utkast"
              className="tw-mr-2 tw-h-6"
            />
            <span className="tw-grow tw-text-raa-green">
              Publiceras efter granskning av personuppgifter och sekretess. Ärende{' '}
              {utkast.registrering.granskning_arendenummer}, inlämnat{' '}
              {formatDateWithTime(utkast.registrering.status_datum_inskickad)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UtkastStatusSection;
